// Homeowner-portal data access via token-validated SECURITY DEFINER RPCs.
//
// Background: the portal used to read/write the `deals` table (and team_roster,
// activity_log, deal_documents, referrals) directly as the anon role, which
// meant anyone with the public anon key could dump/modify the whole dataset.
// These RPCs (migration `homeowner_portal_token_rpcs`) validate the deal token
// server-side — homeowner_name must contain the last name AND the street number
// must match — and only then return/write that one deal. Once the portal uses
// only these, the blanket anon table policies are dropped (Phase 2).
//
// The "triple" (dealId + lastName + streetNum) is exactly what the portal token
// encodes. `authTriple(deal)` re-derives it from a deal already resolved by
// ho_resolve_deal, so it always re-validates cleanly on writes.

import { supabaseAnon as supabase } from "./supabase";

export interface HoTriple {
  dealId: string;
  lastName: string;
  streetNum: string;
}

/** Derive the portal auth triple from a resolved deal. */
export function authTriple(deal: {
  id: string;
  homeowner_name?: string | null;
  property_street?: string | null;
}): HoTriple {
  const parts = (deal.homeowner_name || "").trim().split(/\s+/);
  const lastName = (parts[parts.length - 1] || "").toLowerCase();
  const streetNum = (deal.property_street || "").match(/^(\d+)/)?.[1] || "";
  return { dealId: deal.id, lastName, streetNum };
}

/** Resolve a deal by token components. Returns the deal row (+owner_name) or null. */
export async function hoResolveDeal(dealId: string, lastName: string, streetNum: string) {
  return supabase.rpc("ho_resolve_deal", {
    p_deal_id: dealId,
    p_last_name: lastName,
    p_street_num: streetNum,
  });
}

/** Landing-page lookup: street number + last 4 phone digits -> minimal identity.
 *  Rate limited since Oct 22, 2026: a refusal is HTTP 429 (see homeownerLookup.ts for its words). */
export async function hoFindDeal(streetNum: string, phoneLast4: string) {
  return supabase.rpc("ho_find_deal", {
    p_street_num: streetNum,
    p_phone_last4: phoneLast4,
  });
}

export async function hoMarkDepRelease(t: HoTriple) {
  return supabase.rpc("ho_mark_dep_release", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
  });
}

/**
 * Let the homeowner set or correct their own email (signing page, Oct 16,
 * 2026). The only place a homeowner interacts before the build; emails are
 * blank on ~10% of built deals and never captured at intake. Format-checked
 * server-side; the RPC (migration homeowner_contact_email_rpc) writes
 * deals.homeowner_email only when the token triple validates.
 */
export async function hoUpdateContactEmail(t: HoTriple, email: string) {
  return supabase.rpc("ho_update_contact_email", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_email: email,
  });
}

export async function hoSubmitFeedback(t: HoTriple, rating: number, comment: string) {
  return supabase.rpc("ho_submit_feedback", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_rating: rating,
    p_comment: comment,
  });
}

/**
 * Homeowner pages -> the team (Oct 21, 2026). The status page's message box,
 * its uploads and depreciation-release photos, the upload page, and the two
 * color texts all go through the ho-portal-notify edge function, which checks
 * the link, files the message or files on the deal, and alerts whoever owns
 * the conversation. These pages used to call twilio-send-sms with our own
 * number as the recipient; Twilio refused every one, and the page said "Sent".
 */
export type PortalNotifyEvent = "message" | "upload" | "dep_release" | "colors_locked" | "colors_needed"
  // Oct 29, 2026 (ho-portal-notify v3): the homeowner signed a trade job's add-on; payload { trade_id }.
  | "trade_addendum_signed"
  // Oct 30, 2026 (v3): the signed add-on PDF's URL, made server-side (anon can't read trade-addendums/); payload
  // { pdf_path }, answers { url }.
  | "trade_addendum_url";

export type PortalNotifyResult =
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; code: string; data?: Record<string, unknown> };

export async function hoPortalNotify(
  t: HoTriple,
  event: PortalNotifyEvent,
  extra: Record<string, unknown> = {},
): Promise<PortalNotifyResult> {
  try {
    const { data, error } = await supabase.functions.invoke("ho-portal-notify", {
      body: { deal_id: t.dealId, last_name: t.lastName, street_num: t.streetNum, event, ...extra },
    });
    if (error) {
      // A non-2xx answer: the function's error code is in the response body.
      const ctx = (error as { context?: unknown }).context;
      if (ctx instanceof Response) {
        const body = (await ctx.clone().json().catch(() => null)) as Record<string, unknown> | null;
        if (body && typeof body.error === "string") return { ok: false, code: body.error, data: body };
      }
      return { ok: false, code: "network" };
    }
    const d = (data ?? {}) as Record<string, unknown>;
    if (d.ok === false) return { ok: false, code: typeof d.error === "string" ? d.error : "failed", data: d };
    return { ok: true, data: d };
  } catch {
    return { ok: false, code: "network" };
  }
}

/** What to tell the homeowner when hoPortalNotify didn't go through. */
export function portalNotifyMessage(code: string): string {
  switch (code) {
    case "too_many_messages":
      return "You've sent several messages in the last hour, and we'll get back to you soon. For anything urgent, call or text (470) 823-8816.";
    case "message_too_long":
      return "That message is a little long. Please keep it under 2,000 characters.";
    case "too_many_files":
      return "That's a lot of files at once. Please send up to 20 at a time, or call (470) 823-8816.";
    case "not_authorized":
      return "We couldn't confirm your project link. Please open the link from your text again, or call (470) 823-8816.";
    default:
      return "We couldn't send that. Please try again, or call or text us at (470) 823-8816.";
  }
}

/**
 * Submit a referral from the homeowner portal.
 *
 * Goes through the `referral-submit` edge function rather than a bare RPC: the
 * function writes the structured referral row AND alerts the people who work
 * it (the RM(s) on this deal plus their TL and ASM) on the staff Team line.
 * Postgres can't send SMS, and anon must never be able to read staff phones.
 *
 * Falls back to the ho_create_referral_v2 RPC if the function is unreachable,
 * so a referral is never silently lost — it just won't page anyone.
 */
export async function hoCreateReferral(
  t: HoTriple,
  input: {
    name: string; phone: string; street: string;
    city?: string; state?: string; zip?: string; notes?: string;
  },
): Promise<{ ok: boolean; duplicate?: boolean; notified?: number }> {
  const body = {
    deal_id: t.dealId, last_name: t.lastName, street_num: t.streetNum,
    name: input.name, phone: input.phone, street: input.street,
    city: input.city ?? "", state: input.state ?? "", zip: input.zip ?? "",
    notes: input.notes ?? "",
  };

  const { data, error } = await supabase.functions.invoke("referral-submit", { body });
  if (!error && (data as { ok?: boolean })?.ok) {
    return data as { ok: boolean; duplicate?: boolean; notified?: number };
  }

  const { error: rpcErr } = await supabase.rpc("ho_create_referral_v2", {
    p_deal_id: t.dealId, p_last_name: t.lastName, p_street_num: t.streetNum,
    p_name: input.name, p_phone: input.phone, p_street: input.street,
    p_city: input.city ?? null, p_state: input.state ?? null,
    p_zip: input.zip ?? null, p_notes: input.notes ?? null,
  });
  if (rpcErr) throw rpcErr;
  return { ok: true };
}

/** Referrals this customer has given us, with a friendly status for each.
 *  Narrow projection by design -- see the ho_list_referrals migration. */
export interface HoReferral {
  referred_name: string;
  created_at: string;
  status: string;
  payout_status: string;
  payout_date: string | null;
  payout_amount: number | string | null;
  superseded: boolean;
  /** Where the referred job stands, in plain steps (Oct 20, 2026). Never the
   *  job's stage name. Absent from a response made before that day. */
  progress?: "received" | "contacted" | "scheduled" | "built" | "closed";
}

export async function hoListReferrals(t: HoTriple): Promise<HoReferral[]> {
  const { data, error } = await supabase.rpc("ho_list_referrals", {
    p_deal_id: t.dealId, p_last_name: t.lastName, p_street_num: t.streetNum,
  });
  if (error) throw error;
  return ((data as { referrals?: HoReferral[] })?.referrals ?? []);
}

export async function hoRecordColorAddendum(
  t: HoTriple,
  args: {
    shingle: string;
    drip: string;
    addendumUrl: string;
    pdfPath: string;
    addendumNumber: string;
    signatureUrl: string | null;
    signaturePath: string | null;
  },
) {
  return supabase.rpc("ho_record_color_addendum", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_shingle: args.shingle,
    p_drip: args.drip,
    p_addendum_url: args.addendumUrl,
    p_pdf_path: args.pdfPath,
    p_addendum_number: args.addendumNumber,
    p_signature_url: args.signatureUrl,
    p_signature_path: args.signaturePath,
  });
}

/**
 * A trade job's add-on to the roof agreement (Oct 29, 2026; migration 20261029g). Greg: "if a trade is added after
 * the homeowner has signed, the addendum is made ... the text sends link for them to confirm and sign." The
 * /trade-addendum page reads the deal's add-ons (waiting on the homeowner, or signed) and records the signature.
 * The price to the homeowner and the work's words only: never the floor, the sub's cost or the upside.
 */
export interface HoTradeAddendum {
  trade_id: string;
  label: string;
  amount: number | string | null;
  status: "needs_signature" | "sent" | "signed";
  signed_at: string | null;
  signed_name: string | null;
  number: string | null;
  url: string | null;
  /** The number the next signed add-on on this deal takes (TAA-...-<n>). */
  next_n: number;
}

export async function hoTradeAddenda(t: HoTriple): Promise<HoTradeAddendum[]> {
  const { data, error } = await supabase.rpc("ho_trade_addenda", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
  });
  if (error) throw error;
  return Array.isArray(data) ? (data as HoTradeAddendum[]) : [];
}

/** Refused with "This add-on changed since you opened it. Please reload the page." when the price or the words moved. */
export async function hoRecordTradeAddendum(
  t: HoTriple,
  args: {
    tradeId: string;
    amount: number;
    label: string;
    signedName: string;
    pdfPath: string;
    url: string;
    number: string;
    signaturePath: string | null;
    signatureUrl: string | null;
  },
) {
  return supabase.rpc("ho_record_trade_addendum", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_trade_id: args.tradeId,
    p_amount: args.amount,
    p_label: args.label,
    p_signed_name: args.signedName,
    p_pdf_path: args.pdfPath,
    p_url: args.url,
    p_number: args.number,
    p_signature_path: args.signaturePath,
    p_signature_url: args.signatureUrl,
  });
}

export async function hoSignAgreement(
  t: HoTriple,
  args: {
    shingle: string | null;
    drip: string | null;
    additional: boolean;
    nonCollectAck: boolean;
    agreementNumber: string;
    signatureUrl: string | null;
    signaturePath: string | null;
  },
) {
  return supabase.rpc("ho_sign_agreement", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_shingle: args.shingle,
    p_drip: args.drip,
    p_additional: args.additional,
    p_noncollect_ack: args.nonCollectAck,
    p_agreement_number: args.agreementNumber,
    p_signature_url: args.signatureUrl,
    p_signature_path: args.signaturePath,
  });
}

export async function hoAttachSignedPdf(
  t: HoTriple,
  kind: "agreement" | "loa",
  url: string,
  path: string,
  fileName: string,
) {
  return supabase.rpc("ho_attach_signed_pdf", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
    p_kind: kind,
    p_url: url,
    p_path: path,
    p_file_name: fileName,
  });
}

/** A Stripe bank payment the homeowner typed in by hand and still has to
 *  verify (Oct 20, 2026). */
export interface BankVerification {
  amount: number;
  slot: string | null;
  started_at: string | null;
  verify_url: string | null;
  microdeposit_type: "descriptor_code" | "amounts" | null;
  deposit_arrival_at: string | null;
  verify_deadline_at: string | null;
}

/**
 * Bank payments waiting on the homeowner to confirm Stripe's test deposit
 * (migration 20261020c). Returns { ok, verifications: BankVerification[] }.
 */
export async function hoBankVerifications(t: HoTriple) {
  return supabase.rpc("ho_bank_verifications", {
    p_deal_id: t.dealId,
    p_last_name: t.lastName,
    p_street_num: t.streetNum,
  });
}
